import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import * as Icons from "lucide-react";
import { ICONS_BY_NAME } from "../lib/icons";
import { NotificationBell } from "./NotificationBell";
import { RequestsDrawer } from "./RequestsDrawer";
import { EvaluationModal } from "./Evaluation";
import AuthImg from "./AuthImg";
import { useFocusTrap } from "../hooks/useFocusTrap";
import GuidedTour from "./GuidedTour";
import Container from "./Container";

const SPACE_LABEL = { admin: "Back-office", collaborator: "Espace équipe", client: "Espace entreprise" };

const isActivePath = (loc, to) => (to === "/" ? loc.pathname === "/" : loc.pathname === to || loc.pathname.startsWith(to + "/"));

const ContactAgencyButton = () => {
  const [email, setEmail] = useState("");
  useEffect(() => { api.get("/agency-contact").then((r) => setEmail(r.data.email || "")).catch(() => {}); }, []);
  if (!email) return null;
  const href = `mailto:${email}?subject=${encodeURIComponent("Contact depuis mon espace L'Horizon Metara")}`;
  return (
    <a href={href} data-testid="contact-agency-btn" title="Contacter l'agence"
      className="hidden sm:inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white rounded-full pl-3 pr-4 py-2 text-sm transition-colors border border-white/15">
      <Icons.Mail size={16} /> <span className="whitespace-nowrap">Contacter l'agence</span>
    </a>
  );
};

const UNDERLINE_TRANSITION = { type: "tween", duration: 0.24, ease: [0.22, 1, 0.36, 1] };

const TopItem = ({ to, label, disabled, tip }) => {
  const reduce = useReducedMotion();
  const t = reduce ? { duration: 0 } : UNDERLINE_TRANSITION;
  if (disabled)
    return (
      <div title={tip} data-testid={`nav-${label}`}
        className="flex items-center gap-1.5 px-3 py-2 text-white/60 cursor-not-allowed text-sm whitespace-nowrap">
        <span>{label}</span>
        <Icons.Lock size={16} />
      </div>
    );
  return (
    <NavLink to={to} end={to === "/"} data-testid={`nav-${label}`}
      className={({ isActive }) =>
        `relative flex items-center px-3 py-2 text-sm whitespace-nowrap rounded-sm transition-colors ${
          isActive ? "text-white bg-white/10" : "text-white/65 hover:text-white hover:bg-white/5"
        }`}>
      {({ isActive }) => (
        <>
          <span>{label}</span>
          {isActive && <motion.div layoutId="topnav-underline" className="absolute left-3 right-3 -bottom-[11px] h-[3px] bg-bordeaux rounded-full" transition={t} />}
        </>
      )}
    </NavLink>
  );
};

const NavDropdown = ({ label, children, to }) => {
  const reduce = useReducedMotion();
  const t = reduce ? { duration: 0 } : UNDERLINE_TRANSITION;
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const ref = useRef();
  const btnRef = useRef();
  const nav = useNavigate();
  const loc = useLocation();
  const isActive = (t) => loc.pathname === t || loc.pathname.startsWith(t + "/");
  const active = (to && isActive(to)) || children.some((c) => isActive(c.to));
  const parentBadge = children.reduce((s, c) => s + (c.badge || 0), 0);
  const openMenu = () => { if (btnRef.current) { const r = btnRef.current.getBoundingClientRect(); setPos({ left: r.left, top: r.bottom }); } setOpen(true); };
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  return (
    <div className="relative" ref={ref} onMouseEnter={openMenu} onMouseLeave={() => setOpen(false)}>
      <button ref={btnRef} onClick={() => { if (to) nav(to); else openMenu(); }} data-testid={`nav-${label}`}
        className={`relative flex items-center gap-1.5 px-3 py-2 text-sm whitespace-nowrap rounded-sm transition-colors ${
          active ? "text-white bg-white/10" : "text-white/65 hover:text-white hover:bg-white/5"
        }`}>
        <span>{label}</span>
        {parentBadge > 0 && <span data-testid={`nav-${label}-badge`} className="min-w-[16px] h-4 px-1 rounded-full bg-rouge text-white text-[10px] font-semibold flex items-center justify-center">{parentBadge > 9 ? "9+" : parentBadge}</span>}
        <Icons.ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        {active && <motion.div layoutId="topnav-underline" className="absolute left-3 right-3 -bottom-[11px] h-[3px] bg-bordeaux rounded-full" transition={t} />}
      </button>
      {open && (
        <div data-testid={`nav-${label}-menu`} style={{ position: "fixed", left: pos.left, top: pos.top }} className="pt-2 z-[80]">
          <div className="w-60 bg-white rounded-sm shadow-xl border border-marine/10 py-1.5 animate-in fade-in slide-in-from-top-1">
            {children.map((c) => {
              const cActive = isActive(c.to);
              return (
                <button key={c.to} onClick={() => nav(c.to)} data-testid={`nav-${c.label}`}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left transition-colors ${cActive ? "text-bordeaux bg-bordeaux-50" : "text-marine hover:bg-sable-clair"}`}>
                  <span>{c.label}</span>
                  {c.badge > 0 && <span data-testid={`nav-${c.label}-badge`} className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-rouge text-white text-[10px] font-semibold flex items-center justify-center">{c.badge > 99 ? "99+" : c.badge}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const NavDrawerItem = ({ item, onNavigate }) => {
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  if (item.disabled) {
    return (
      <div title={item.tip} data-testid={`drawer-nav-${item.label}`}
        className="px-5 py-3.5 text-sm text-marine/35 border-b border-marine/10 flex items-center gap-1.5">
        {item.label} <Icons.Lock size={16} />
      </div>
    );
  }

  if (item.children) {
    const active = (item.to && isActivePath(loc, item.to)) || item.children.some((c) => isActivePath(loc, c.to));
    const parentBadge = item.children.reduce((s, c) => s + (c.badge || 0), 0);
    return (
      <div className="border-b border-marine/10">
        <button onClick={() => setOpen((o) => !o)} data-testid={`drawer-nav-${item.label}`}
          className="w-full flex items-center justify-between px-5 py-3.5 text-left">
          <span className={`text-sm font-medium ${active ? "text-bordeaux" : "text-marine"}`}>{item.label}</span>
          <span className="flex items-center gap-2">
            {parentBadge > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-rouge text-white text-[10px] font-semibold flex items-center justify-center">{parentBadge > 99 ? "99+" : parentBadge}</span>}
            <Icons.ChevronDown size={16} className={`transition-transform text-marine ${open ? "rotate-180" : ""}`} />
          </span>
        </button>
        {open && (
          <div className="pb-2 bg-marine/[0.03]">
            {item.children.map((c) => (
              <button key={c.to} onClick={() => onNavigate(c.to)} data-testid={`drawer-nav-${c.label}`}
                className={`w-full flex items-center justify-between text-left pl-9 pr-5 py-2.5 text-sm ${isActivePath(loc, c.to) ? "text-bordeaux font-medium" : "text-marine/80"}`}>
                <span>{c.label}</span>
                {c.badge > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-rouge text-white text-[10px] font-semibold flex items-center justify-center">{c.badge > 99 ? "99+" : c.badge}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <button onClick={() => onNavigate(item.to)} data-testid={`drawer-nav-${item.label}`}
      className={`w-full text-left px-5 py-3.5 text-sm border-b border-marine/10 ${isActivePath(loc, item.to) ? "text-bordeaux font-medium" : "text-marine"}`}>
      {item.label}
    </button>
  );
};

const NavDrawer = ({ open, onClose, items, user, profilePath, onLogout, helpPath }) => {
  const ref = useRef();
  const nav = useNavigate();
  const [requestsUnread, setRequestsUnread] = useState(0);

  useEffect(() => {
    const h = (e) => setRequestsUnread(e.detail?.count || 0);
    window.addEventListener("requests-unread-changed", h);
    return () => window.removeEventListener("requests-unread-changed", h);
  }, []);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    if (open) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open, onClose]);

  useFocusTrap(ref, open, onClose);

  if (!open) return null;

  const goTo = (path) => { onClose(); nav(path); };
  const openRequests = () => { onClose(); window.dispatchEvent(new CustomEvent("open-requests")); };

  return (
    <div className="fixed inset-0 z-[65] bg-black/30 lg:hidden" data-testid="nav-drawer-overlay">
      <div ref={ref} tabIndex={-1} data-testid="nav-drawer"
        className="absolute right-0 top-0 h-full w-full max-w-xs bg-creme shadow-2xl flex flex-col animate-in slide-in-from-right duration-200 outline-none">
        <div className="flex items-center justify-between px-5 h-16 border-b border-marine/10 bg-white shrink-0">
          <div className="font-display text-lg text-marine">Menu</div>
          <button onClick={onClose} data-testid="nav-drawer-close" aria-label="Fermer le menu">
            <Icons.X size={16} className="text-marine" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {items.map((item) => <NavDrawerItem key={item.label} item={item} onNavigate={goTo} />)}
          <button onClick={openRequests} data-testid="drawer-nav-Demandes"
            className="w-full flex items-center justify-between px-5 py-3.5 text-sm text-marine border-b border-marine/10">
            <span>{user?.role === "admin" ? "À traiter" : "Demandes"}</span>
            {requestsUnread > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-ambre text-white text-[10px] font-semibold flex items-center justify-center">{requestsUnread > 9 ? "9+" : requestsUnread}</span>}
          </button>
          {helpPath && (
            <button onClick={() => goTo(helpPath)} data-testid="drawer-nav-Aide"
              className="w-full flex items-center justify-between px-5 py-3.5 text-sm text-marine border-b border-marine/10">
              <span>Aide</span>
            </button>
          )}
        </div>
        <div className="border-t border-marine/10 py-2 shrink-0">
          <button onClick={() => goTo(profilePath)} data-testid="drawer-nav-profile"
            className="w-full flex items-center gap-2.5 px-5 py-3 text-sm text-marine">
            <Icons.UserCircle size={16} strokeWidth={1.75} /> Profil utilisateur
          </button>
          {user.role === "admin" && (
            <button onClick={() => goTo("/parametres")} data-testid="drawer-nav-settings"
              className="w-full flex items-center gap-2.5 px-5 py-3 text-sm text-marine">
              <Icons.Settings size={16} strokeWidth={1.75} /> Paramètres
            </button>
          )}
          <button onClick={() => { onClose(); onLogout(); }} data-testid="drawer-nav-logout"
            className="w-full flex items-center gap-2.5 px-5 py-3 text-sm text-rouge">
            <Icons.LogOut size={16} strokeWidth={1.75} /> Déconnexion
          </button>
        </div>
      </div>
    </div>
  );
};

const ProfileMenu = ({ user, profilePath, onLogout }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const nav = useNavigate();
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const go = (path) => { setOpen(false); nav(path); };
  const MenuRow = ({ icon, label, onClick, testid, danger }) => {
    const Ic = ICONS_BY_NAME[icon];
    return (
      <button onClick={onClick} data-testid={testid}
        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left transition-colors ${danger ? "text-rouge hover:bg-rouge-50" : "text-marine hover:bg-sable-clair"}`}>
        <Ic size={16} strokeWidth={1.75} /> {label}
      </button>
    );
  };
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid="profile-menu-btn"
        className="hidden lg:flex w-9 h-9 rounded-full bg-white/15 text-white items-center justify-center text-sm font-display overflow-hidden hover:ring-2 hover:ring-white/40 transition-all">
        {user.avatar_url ? <AuthImg path={user.avatar_url} alt={user.name} className="w-full h-full object-cover" /> : (user.name?.[0]?.toUpperCase() || "?")}
      </button>
      {open && (
        <div data-testid="profile-menu" className="absolute right-0 mt-2 w-64 bg-white rounded-sm shadow-xl border border-marine/10 py-2 z-[70] animate-in fade-in slide-in-from-top-1">
          <div className="px-4 py-3 border-b border-marine/10">
            <div className="font-display text-base text-marine leading-tight">{user.name}</div>
            {user.job_role && <div className="text-xs text-bordeaux">{user.job_role}</div>}
            <div className="text-xs text-marine/80 truncate mt-0.5">{user.email}</div>
          </div>
          <MenuRow icon="UserCircle" label="Profil utilisateur" onClick={() => go(profilePath)} testid="menu-profile" />
          {user.role === "admin" && <MenuRow icon="Settings" label="Paramètres" onClick={() => go("/parametres")} testid="menu-settings" />}
          <div className="border-t border-marine/10 my-1" />
          <MenuRow icon="LogOut" label="Déconnexion" onClick={() => { setOpen(false); onLogout(); }} testid="logout-btn" danger />
        </div>
      )}
    </div>
  );
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const doLogout = async () => { await logout(); nav("/login"); };

  const [navDrawerOpen, setNavDrawerOpen] = useState(false);
  useEffect(() => { setNavDrawerOpen(false); }, [loc.pathname]);

  const [failedEmails, setFailedEmails] = useState(0);
  useEffect(() => {
    if (user.role !== "admin") return;
    let active = true;
    const fetchCount = () => api.get("/admin/email-logs/failed-count").then((r) => active && setFailedEmails(r.data.count || 0)).catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { active = false; clearInterval(t); };
  }, [user.role]);
  useEffect(() => {
    if (user.role === "admin") api.get("/admin/email-logs/failed-count").then((r) => setFailedEmails(r.data.count || 0)).catch(() => {});
  }, [loc.pathname, user.role]);

  let items = [];
  if (user.role === "client") {
    const mods = user.active_modules || [];
    items = [
      { to: "/", icon: "LayoutDashboard", label: "Tableau de bord" },
      { to: "/projets", icon: "FolderKanban", label: "Mes Projets" },
      // Charte : seulement quand le module est actif (recette du 15/10 ; décision de Mélilia, question 13) — jusque-là,
      // l'« Accès rapide » du tableau de bord était le seul chemin.
      ...(mods.includes("identite_visuelle") ? [{ to: "/identite", icon: "Palette", label: "Charte" }] : []),
      { to: "/calendrier", icon: "CalendarDays", label: "Réseaux sociaux", key: "reseaux_sociaux" },
      { to: "/parametres", icon: "Settings", label: "Paramètres" },
    ].map((i) => ({ ...i, disabled: i.key && !mods.includes(i.key), tip: "Non activé · Contactez Metara" }));
  } else if (user.role === "collaborator") {
    items = [
      { to: "/", icon: "LayoutDashboard", label: "Tableau de bord" },
      { to: "/missions", icon: "ClipboardList", label: "Missions" },
      // Entreprises ouvertes par l'agence (recette du 15/10 ; décision de Mélilia, question 12) : l'entrée avait disparu
      // le 17/07 dans un enregistrement automatique, sans décision ; même condition qu'avant.
      ...((user.allowed_clients || []).length > 0 ? [{ to: "/clients", icon: "Users", label: "Entreprises" }] : []),
      { to: "/calendrier", icon: "CalendarDays", label: "Calendrier" },
    ];
  } else {
    items = [
      { to: "/", icon: "LayoutDashboard", label: "Tableau de bord" },
      { label: "Clients", icon: "Users", children: [
        { to: "/admin/clients", icon: "Building2", label: "Entreprises" },
        { to: "/admin/contacts", icon: "Contact2", label: "Contacts" },
        // L'annuaire des tiers qu'on cite (27/10) : ni des clients, ni des personnes — des marques.
        { to: "/admin/entites", icon: "Landmark", label: "Entités" },
      ] },
      { label: "Modules", icon: "LayoutGrid", to: "/admin/modules", children: [
        { to: "/admin/projets", icon: "FolderKanban", label: "Tous les projets" },
        { to: "/admin/missions", icon: "Clapperboard", label: "Toutes les missions" },
      ] },
      { to: "/admin/calendrier", icon: "CalendarDays", label: "Calendrier éditorial" },
      { to: "/admin/collaborateurs", icon: "UserCog", label: "Collaborateurs" },
      { to: "/admin/comptabilite", icon: "Wallet", label: "Comptabilité", children: [
        { to: "/admin/comptabilite", icon: "LayoutDashboard", label: "Cockpit financier" },
        { to: "/admin/comptabilite/factures", icon: "ReceiptText", label: "Factures clients" },
        { to: "/admin/comptabilite/devises", icon: "Coins", label: "Devises & Taux" },
      ] },
      { label: "Automatisations", icon: "Zap", children: [
        { to: "/admin/automations", icon: "Mail", label: "E-mails" },
        { to: "/admin/journal-emails", icon: "ScrollText", label: "Journal des e-mails", badge: failedEmails },
        { to: "/admin/automations-taches", icon: "ListChecks", label: "Tâches" },
        { to: "/admin/automations-workflows-prestation", icon: "GitBranch", label: "Workflows prestations" },
        { to: "/admin/automations-workflows-mission", icon: "Workflow", label: "Workflows missions" },
        { to: "/admin/automations-formulaires", icon: "FileText", label: "Formulaires" },
        { to: "/admin/automations-regles", icon: "SlidersHorizontal", label: "Règles" },
        { to: "/admin/assistant", icon: "Sparkles", label: "Assistant" },
      ] },
    ];
  }

  const profilePath = user.role === "client" ? "/compte" : "/profil";
  const helpPath = user.role === "admin" ? "/admin/aide" : "/aide";
  const onHelp = loc.pathname === helpPath;

  return (
    <div className="min-h-screen beige-texture">
      <header className="bg-marine sticky top-0 z-50 shadow-md" data-testid="top-nav">
        <div className="px-4 lg:px-6 flex items-center gap-4 h-16">
          <div className="flex items-center gap-3 shrink-0">
            <div className="leading-none">
              <div className="font-display text-white text-base">L'Horizon <span className="text-white/90">Metara</span></div>
              <div className="text-[9px] uppercase tracking-[0.25em] text-creme">{SPACE_LABEL[user.role]}</div>
            </div>
          </div>
          <div className="h-8 w-px bg-white/15 shrink-0 hidden lg:block" />
          <nav aria-label="Menu principal" className="hidden lg:flex items-center gap-1 flex-1 overflow-x-auto no-scrollbar" data-testid="main-nav">
            {items.map((i) => i.children ? <NavDropdown key={i.label} label={i.label} icon={i.icon} to={i.to} children={i.children} /> : <TopItem key={i.label} to={i.to} icon={i.icon} label={i.label} disabled={i.disabled} tip={i.tip} />)}
          </nav>
          <div className="flex items-center gap-3 shrink-0 ml-auto lg:ml-0">
            {(user.role === "client" || user.role === "collaborator") && <ContactAgencyButton />}
            <RequestsDrawer />
            <NotificationBell />
            <ProfileMenu user={user} profilePath={profilePath} onLogout={doLogout} />
            <button onClick={() => setNavDrawerOpen(true)} data-testid="nav-drawer-open" aria-label="Menu de navigation"
              className="lg:hidden w-9 h-9 rounded-full flex items-center justify-center text-white/85 hover:bg-white/10 hover:text-white transition-colors">
              <Icons.Menu size={16} />
            </button>
          </div>
        </div>
      </header>
      <NavDrawer open={navDrawerOpen} onClose={() => setNavDrawerOpen(false)} items={items} user={user} profilePath={profilePath} onLogout={doLogout} helpPath={helpPath} />
      <main className="min-h-[calc(100vh-4rem)]">
        {/* F1 (décision du 18/08, exécutée le 30/09) : une seule largeur de page, 1 280 px, pour tout le site. */}
        <Container className="py-10 lg:py-14">{children}</Container>
      </main>
      <EvaluationModal />
      {user.role === "client" && <GuidedTour />}
      {/* Sur téléphone et tablette, « Aide » est une ligne du menu et ce bouton n'apparaît pas (décision de Mélilia du
          01/10) : fixe en bas à droite, il recouvrait le bord droit de ce qui défilait dessous.
          Niveau 30 (recette du 15/10, ANO-10) : au-dessus du contenu des pages (10 au plus), sous tout ce qui s'ouvre
          par-dessus — volet des demandes et fenêtres ouverts depuis l'en-tête (enfermés à son niveau 50), panneaux (40),
          fenêtres (50 à 90). À 50, il recouvrait « Envoyer » du volet des demandes. */}
      {!onHelp && (
        <button onClick={() => nav(helpPath)} data-testid="floating-help-btn" title="Aide" aria-label="Aide"
          className="group fixed bottom-6 right-6 z-30 hidden lg:flex items-center gap-2 bg-marine text-white rounded-full shadow-lg hover:bg-marine-hover hover:shadow-xl transition-all pl-4 pr-5 py-3 hover:-translate-y-0.5">
          <Icons.LifeBuoy size={16} />
          <span className="text-sm font-medium max-w-0 overflow-hidden opacity-0 group-hover:max-w-[80px] group-hover:opacity-100 transition-all duration-300 whitespace-nowrap">Aide</span>
        </button>
      )}
    </div>
  );
}

export const PageTitle = ({ overline, title, action }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
    <div>
      {overline && <div className="text-xs uppercase tracking-[0.22em] font-semibold text-bordeaux mb-2.5">{overline}</div>}
      <h1 className="font-display text-3xl lg:text-[2.5rem] leading-[1.05] text-marine">{title}</h1>
    </div>
    {action}
  </div>
);
